import { memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import {
  CallIntelChip,
  CallIntelTooltip,
  generateTrackingId,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { Chart, CustomLegendsData, PieChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { ChartInsightCard } from '../chart-insight-card';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { useUnhappyServiceIssueInsight } from '../hooks/use-unhappy-service-issue-insight';

type FormattedChartData = {
  pie: PieChartData;
  sneakPeak: { legendId: string; name: string; value: number }[];
  percentages: Record<string, number>;
  values: Record<string, number>;
};

type Sentiments = Record<CallIntelTypes.SentimentEnum | string, number>;

type SentimentsChartProps = {
  internalPage?: boolean;
  isLoading?: boolean;
  sentiments?: Sentiments | null;
  showChartInsights?: boolean;
  stats?: CallIntelTypes.ServiceQualityStats;
};

const DEFAULT_ORDER: CallIntelTypes.SentimentEnum[] = [
  CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
  CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
  CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
];

const PIE_APPEARANCE = {
  borderRadius: 8,
  height: 180,
  innerRadius: 70,
  minWidth: 180,
  outerRadius: 88,
};

const orderSentiments = (sentiments: Sentiments = {}): Sentiments =>
  DEFAULT_ORDER.reduce<Sentiments>((acc, key) => {
    acc[key] = sentiments[key] ?? 0;
    return acc;
  }, {});

const useChartData = (
  sentiments?: Sentiments,
  sentiMentLabels?: Record<CallIntelTypes.SentimentEnum | string, string>
): FormattedChartData => {
  const orderedSentiments = orderSentiments(sentiments);
  const total = Object.values(orderedSentiments).reduce((acc, value) => acc + value, 0);

  return useMemo(
    () =>
      Object.entries(orderedSentiments).reduce(
        (acc, [id, value]) => {
          acc.pie.centerMetric = total;

          acc.pie.groups.push({
            name: id,
            value,
          });

          acc.sneakPeak.push({
            legendId: id,
            name: sentiMentLabels ? sentiMentLabels[id] ?? id : id,
            value,
          });

          acc.percentages[id] = value / total;
          acc.values[id] = value;

          return acc;
        },
        {
          pie: {
            groups: [],
          },
          sneakPeak: [],
          percentages: {},
          values: {},
        } as FormattedChartData
      ),
    [orderedSentiments, sentiMentLabels, total]
  );
};

export const SentimentsChart = memo(
  ({ isLoading, sentiments, internalPage, showChartInsights, stats }: SentimentsChartProps) => {
    const { t } = useTranslation('analytics');
    const navigate = useNavigate();
    const { showDemoChipAndBanner } = useCallIntelDemoFlags();
    const { chipVariants, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
      'chipVariants',
      'dataColors',
      'dataLabels'
    );
    const { setSubView, subView } = useCallIntelShallowStore('setSubView', 'subView');
    const { filterHintText } = useActiveFilters();

    const insight = useUnhappyServiceIssueInsight({ sentiments, stats });

    const chartData = useChartData(sentiments || {}, dataLabels.sentiments);

    const isEmptyValue = useMemo(
      () => DEFAULT_ORDER.every((key) => !((sentiments ?? {})[key] && (sentiments ?? {})[key] > 0)),
      [sentiments]
    );

    const chartTitle = useMemo(() => {
      if (internalPage && subView.id) {
        const subViewLabel =
          subView.type === 'appointment-type'
            ? dataLabels.appointmentTypes?.[subView.id]
            : dataLabels.categories?.[subView.id];

        return t('{{name}} Calls - Customer Sentiment', { name: subViewLabel });
      }
      return t('Customer Sentiment');
    }, [internalPage, subView, dataLabels, t]);

    const legendData = useMemo<CustomLegendsData>(
      () =>
        DEFAULT_ORDER.reduce((acc, sentiment) => {
          acc[sentiment] = {
            label: (
              <div css={styles.legendLabel}>
                <CallIntelChip as='chip' chipVariants={chipVariants} dataKey={sentiment} label='' />
                <Text size='small' color='subdued'>
                  {dataLabels.sentiments?.[sentiment]}
                </Text>
              </div>
            ),
            color: dataColors.sentiments?.[sentiment] || '',
            value: formatters.value(sentiments?.[sentiment]),
          };
          return acc;
        }, {} as CustomLegendsData),
      [chipVariants, dataColors.sentiments, dataLabels.sentiments, sentiments]
    );

    const onSegmentClick = useCallback(
      ({ name }: { name: string }) => {
        if (internalPage) return;
        setSubView({ id: name, type: 'sentiment' });
        navigate({
          to: '/analytics/call-intelligence/$id',
          params: { id: name.toLowerCase() },
          search: { type: 'sentiment' },
        });
      },
      [internalPage, navigate, setSubView]
    );

    const renderCustomTooltipTitle = useCallback(
      ({ groupName }: { groupName: string }) => (
        <CustomTooltipTitle addPrefix={internalPage} label={dataLabels.sentimentsWithEmoji?.[groupName]} />
      ),
      [dataLabels.sentimentsWithEmoji, internalPage]
    );

    const getCustomTooltipData = useCallback(
      (id: string) => [
        {
          color: dataColors.sentiments?.[id] || '',
          formattedValue: formatters.percent(chartData.percentages[id]),
          id,
          label: t('of Calls'),
        },
        {
          color: dataColors.sentiments?.[id] || '',
          formattedValue: formatters.value(sentiments?.[id]),
          id,
          label: t('Calls'),
        },
      ],
      [chartData.percentages, dataColors.sentiments, sentiments, t]
    );

    return (
      <Chart
        bottomContentRenderer={
          showChartInsights && (
            <ChartInsightCard
              title={insight.title}
              subtitle={insight.subTitle}
              muted={insight.counts.callsAnalyzed === 0}
              trackingId={generateTrackingId({ component: 'sentiments-chart-insight-card', context: 'click' })}
            />
          )
        }
        css={styles.chartStyles}
        colors={dataColors.sentiments || {}}
        isLoading={isLoading}
        labels={dataLabels.sentiments || {}}
        style={{ marginBottom: 0 }}
      >
        <Chart.HeaderV2
          title={
            <Chart.HeaderV2.Title
              leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
              infoTip={!internalPage && <CallIntelTooltip tip='sentiments' />}
            >
              {chartTitle}
            </Chart.HeaderV2.Title>
          }
          action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
          subTitle={
            internalPage ? null : (
              <Text color='subdued' size='medium'>
                {t('Click each segment to learn more.')}
              </Text>
            )
          }
        />
        <Chart.PieChart
          appearance={{
            ...PIE_APPEARANCE,
            customTooltipTitle: renderCustomTooltipTitle,
            customTooltipData: getCustomTooltipData,
          }}
          data={sentiments ? chartData.pie : { groups: [] }}
          formatValue={formatters.value}
          {...(!internalPage && {
            onClick: onSegmentClick,
            baseTrackingId: generateTrackingId({ component: 'customer-sentiment-pie-chart' }),
          })}
        />
        {!isEmptyValue ? (
          <Chart.Legends
            allowClickOnCustomLegends
            customData={legendData}
            formatValue={formatters.value}
            size='small'
            values={chartData.values}
          />
        ) : (
          <Text color='subdued' css={styles.emptyText}>
            {t('No data available')}
          </Text>
        )}
      </Chart>
    );
  }
);

SentimentsChart.displayName = 'SentimentsChart';

const styles = {
  legendLabel: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  chartStyles: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};

    .chart-content {
      border: none;
      padding: ${theme.spacing(3)};
    }
  `,
  emptyText: css`
    text-align: center;
  `,
};
